import { ServiceRequest } from "./ServiceRequest";
import { getProductLineByPolicyType } from "js/utilities";
import { AgentData } from "context/Agent/DataProvider";
import { useBreadcrumb } from "hooks/useBreadcrumb";
import { useState, useEffect } from "react";
import { getTasks } from "service/policy/getTasks";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useContext } from "react";
import {
  convertToTitleCase,
  getServiceRequestDate,
  odsClaimStatusConversion,
} from "js/formatting";

import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import PolicyLookUpFromComboBoxModal from "components/AgentPortal/ModalDialogs/PolicyLookUpFromComboBoxModal";
import PolicyLookupModal from "components/AgentPortal/ModalDialogs/PolicyLookupModal";
import AppNavigationCard from "components/General/AppNavigationCard";
import AgentAutoCompleteComboBox from "components/General/AutoCompleteComboBox/AgentAutoCompleteComboBox";
import config from "config/config";

export default function () {
  const { t } = useTranslation();
  const [isOpenPolicyLookup, setIsOpenPolicyLookup] = useState(false);
  const { user } = useSelector((s) => s.user);
  const { agentPolicies: policies } = useContext(AgentData);
  const [policyFilterOptions, setPolicyFilterOptions] = useState([]);
  const [selectedPolicyId, setSelectedPolicyId] = useState("all");
  const [allServices, setAllServices] = useState([]);
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [modelState, setModelState] = useState(false);

  useEffect(() => {
    let policySelectionArrayForDocTab = [];
    policies.map((p) => {
      policySelectionArrayForDocTab.push({
        value: p.contractID,
        status: p.status,
        displayAsValue: p.oldPolicy,
        displayAs: `${p.oldPolicy}: ${p?.insuredFullName}`,
      });
      setPolicyFilterOptions([
        {
          value: "all",
          displayAs: t("AgentPolicySelector-alloption"),
          status: "",
          displayAsValue: t("AgentPolicySelector-alloption"),
        },
        ...policySelectionArrayForDocTab,
      ]);
    });
  }, [policies]);

  const getOldPolicy = (contractID) => {
    let policy = policies?.find((p) => p.contractID === contractID);
    if (policy) return policy.oldPolicy;
    return "";
  };

  useEffect(() => {
    setIsLoading(true);
    getTasks({
      contractId_array: [],
      agentId: user?.agentID,
    })
      .then((response) => {
        const restrictedProcessServices =
          config.RESTRICTED_PROCESS_SERVICE_REQUEST.split(",");
        let serviceList = [];
        //sort newest to oldest
        response?.sort(function (a, b) {
          const firstDate = getServiceRequestDate(a);
          const secondDate = getServiceRequestDate(b);
          if (new Date(firstDate) > new Date(secondDate)) {
            return -1;
          }
          if (new Date(firstDate) < new Date(secondDate)) {
            return 1;
          }
          return 0;
        });
        response.forEach((task) => {
          if (
            !restrictedProcessServices.includes(task.process.toLowerCase()) &&
            task?.status?.toLowerCase() !== "e"
          ) {
            const [statusClass, statusText] = odsClaimStatusConversion(
              task.status,
              "serviceRequest"
            );

            serviceList.push({
              desc: convertToTitleCase(task.desc),
              oldPolicy: getOldPolicy(task.policyID.substring(2)),
              productType: getProductLineByPolicyType(
                task.policyID.substring(2)
              ),
              statusClass: statusClass,
              statusText: statusText,
              contractID: task.policyID.substring(2),
              taskID: task.taskID,
              receivedDate: getServiceRequestDate(task),
            });
          }
        });

        if (serviceList?.length > 0) {
          setServices(serviceList);
          setAllServices(serviceList);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.error(
          "Server was unable to connect, please try again later.",
          error
        );
        setIsLoading(false);
      });
  }, [user]);
  useBreadcrumb([
    { name: t("AgentServices-home"), url: "/dashboard" },
    { name: t("AgentServices-services"), url: "#" },
  ]);

  return (
    <EverlakeLoadingOverlay
      isLoading={isLoading}
      loadingText={t("AgentServices-loading-text")}
    >
      <div className="container ui-component-spacing">
        <h1>{t("AgentServices-title")}</h1>
        <hr />
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 auto-rows-fr">
          <AppNavigationCard
            icon={t("AgentServices-link1-icon")}
            title={t("AgentServices-link1-title")}
            desc={t("AgentServices-link1-dsc")}
            type={t("AgentServices-link1-type")}
            openDialog={() => setModelState(true)}
          />
          <AppNavigationCard
            icon={t("AgentServices-link2-icon")}
            title={t("AgentServices-link2-title")}
            desc={t("AgentServices-link2-dsc")}
            url={"/services-claims/forms"}
          />
          <AppNavigationCard
            icon={t("AgentServices-link3-icon")}
            title={t("AgentServices-link3-titles")}
            desc={t("AgentServices-link3-dsc")}
            type={t("AppNavigationCard-policyLookUp-type")}
            openDialog={() => setIsOpenPolicyLookup(true)}
          />
          <AppNavigationCard
            icon={t("AgentServices-link4-icon")}
            title={t("AgentServices-link4-title")}
            desc={t("AgentServices-link4-desc")}
            url={"/payment"}
          />
        </div>
      </div>
      <div className="container ui-component-spacing">
        <h2 className="mb-3">{t("AgentDashboard-service-title")}</h2>
        <div className="flex justify-between mb-6">
          <div className="md:w-1/3">
            <AgentAutoCompleteComboBox
              labelName={t("AgentBookOfBusiness-dropdownLabel")}
              inputPlaceholderText={t("AgentFACStepOne-policyselect")}
              options={policyFilterOptions}
              selectedOption={selectedPolicyId}
              setSelectedOption={(option) => {
                setSelectedPolicyId(option?.value);
                if (option?.value !== "all") {
                  const filtered = allServices.filter(
                    (s) => s.contractID === option?.value
                  );
                  setServices(filtered);
                } else {
                  setServices(allServices);
                }
              }}
            />
          </div>
          <p className="self-end text-sm font-normal">
            {t("CustomerServices-serviceRequest-footer")}
          </p>
        </div>
        <ServiceRequest data={services} setOpenChatWindow={false} />
      </div>
      {/* Policy Lookup Dialog */}
      <PolicyLookupModal
        isOpenPolicyLookup={isOpenPolicyLookup}
        setIsOpenPolicyLookup={setIsOpenPolicyLookup}
      />
      <PolicyLookUpFromComboBoxModal
        isOpenPolicyLookup={modelState}
        setIsOpenPolicyLookup={setModelState}
      />
    </EverlakeLoadingOverlay>
  );
}
